import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
} from "@mui/icons-material";

const cardBaseSx = {
  borderRadius: 2,
  boxShadow: "0 10px 40px rgba(15,23,42,0.04)",
  border: "1px solid #e2e8f0",
};

export default function CompanyDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompany();
  }, [id]);

  const fetchCompany = async () => {
    try {
      const response = await axios.get(`${API}/admin/companies/${id}`);
      setCompany(response.data);
    } catch (error) {
      toast.error("Erro ao carregar empresa");
      navigate("/admin/companies");
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      periodo_teste: { label: "Período de Teste", color: "warning" },
      em_dia: { label: "Em Dia", color: "success" },
      em_atraso: { label: "Em Atraso", color: "error" },
      bloqueada: { label: "Bloqueada", color: "error" },
    };

    const config = statusConfig[status] || { label: status, color: "default" };
    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString("pt-BR") : "-";

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "60vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!company) {
    return (
      <Box textAlign="center" py={8}>
        <Typography variant="h6">Empresa não encontrada</Typography>
        <Button onClick={() => navigate("/admin/companies")} sx={{ mt: 2 }}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <Box
      data-testid="company-details-page"
      sx={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          mb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Esquerda: título/subtítulo */}
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {company.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Detalhes da empresa
          </Typography>
        </Box>

        {/* Direita: voltar + ações */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Tooltip title="Voltar para lista" arrow>
            <IconButton
              onClick={() => navigate("/admin/companies")}
              size="small"
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
              }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            onClick={() => navigate(`/admin/companies/${company.id}/users`)}
          >
            Gerenciar Usuários
          </Button>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/admin/companies/${company.id}/edit`)}
          >
            Editar
          </Button>
        </Box>
      </Box>

      {/* LAYOUT EM 2 COLUNAS – preenche 100% da largura */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "minmax(0, 2.5fr) minmax(0, 1fr)",
          },
          gap: 3,
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        {/* COLUNA ESQUERDA */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Informações da Empresa */}
          <Card sx={cardBaseSx}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Informações da Empresa
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                  columnGap: 3,
                  rowGap: 2,
                }}
              >
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Nome
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {company.name}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    CNPJ
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {company.cnpj || "-"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {company.email}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Telefone
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {company.phone || "-"}
                  </Typography>
                </Box>
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography variant="body2" color="text.secondary">
                    Endereço
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {company.address || "-"}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Usuários */}
          <Card sx={cardBaseSx}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Usuários ({company.users?.length || 0})
              </Typography>
              <Divider sx={{ my: 2 }} />

              {company.users && company.users.length > 0 ? (
                <TableContainer>
                  <Table
                    size="small"
                    sx={{
                      borderCollapse: "separate",
                      borderSpacing: "0 6px",
                      "& thead th": {
                        backgroundColor: "grey.50",
                        fontSize: 12,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        color: "text.secondary",
                        borderBottom: "none",
                      },
                      "& tbody td": {
                        borderBottom: "none",
                        backgroundColor: "background.paper",
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Nome</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell align="center">Perfil</TableCell>
                        <TableCell align="center">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {company.users.map((user) => (
                        <TableRow key={user.id} hover>
                          <TableCell>
                            <Typography fontWeight={500}>
                              {user.name}
                            </Typography>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                user.role === "company_supervisor"
                                  ? "Supervisor"
                                  : "Usuário"
                              }
                              size="small"
                              color={
                                user.role === "company_supervisor"
                                  ? "primary"
                                  : "default"
                              }
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={user.active ? "Ativo" : "Inativo"}
                              size="small"
                              color={user.active ? "success" : "default"}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Nenhum usuário cadastrado
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* COLUNA DIREITA */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Status */}
          <Card sx={cardBaseSx}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Status
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  Status da Empresa
                </Typography>
                <Chip
                  label={company.active ? "Ativa" : "Inativa"}
                  color={company.active ? "success" : "default"}
                  size="small"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                >
                  Status de Pagamento
                </Typography>
                {getStatusChip(company.payment_status)}
              </Box>

              {company.trial_ends_at && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Fim do Período de Teste
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(company.trial_ends_at)}
                  </Typography>
                </Box>
              )}

              {company.payment_due_date && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Próximo Vencimento
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(company.payment_due_date)}
                  </Typography>
                </Box>
              )}

              {company.last_payment_date && (
                <Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Último Pagamento
                  </Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {formatDate(company.last_payment_date)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Estatísticas */}
          <Card sx={cardBaseSx}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                Estatísticas
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Produtos
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {company.products_count || 0}
                </Typography>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Clientes
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {company.customers_count || 0}
                </Typography>
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary">
                  Vendas
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {company.sales_count || 0}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
